/**
 * src/Pages/Resources/data/resourcesData.js
 * ---------------------------------------------------------------------------
 * Static documentation content for the WebTwin Resources pages.
 *
 * All platform capabilities described here are sourced from:
 *   - WebTwin Platform Overview documentation
 *   - Existing codebase implementation (permissions, CMS workflow)
 *
 * Structure is designed for easy future migration to a CMS/API.
 * ---------------------------------------------------------------------------
 */

export const DEMO_DESTINATION = "#contact";

// ─── Sidebar Navigation ────────────────────────────────────────────────────────

export const SIDEBAR_SECTIONS = [
  {
    group: 'WebTwin',
    items: [
      { label: 'Overview', path: '/resources', slug: 'overview' },
    ],
  },
  {
    group: 'Immersive Experiences',
    items: [
      { label: 'Interactive 3D & Digital Twins', path: '/resources/interactive-3d', slug: 'interactive-3d' },
      { label: 'AR Experiences', path: '/resources/ar-experiences', slug: 'ar-experiences' },
      { label: '360° Virtual Tours', path: '/resources/360-tours', slug: '360-tours' },
    ],
  },
  {
    group: 'Management',
    items: [
      { label: 'Role-Based Access Control', path: '/resources/access-control', slug: 'access-control' },
      { label: 'Publish Controls', path: '/resources/publish-controls', slug: 'publish-controls' },
      { label: 'Instant Updates', path: '/resources/instant-updates', slug: 'instant-updates' },
    ],
  },
];

// ─── Documentation Content ──────────────────────────────────────────────────────

/**
 * Each page has:
 *   - title:       Page heading (h1)
 *   - subtitle:    Brief tagline below title
 *   - keywords:    Search aliases (not displayed)
 *   - sections:    Array of content blocks rendered sequentially
 *
 * Section types:
 *   - heading:     { type: 'heading', level: 2|3, text, id }
 *   - paragraph:   { type: 'paragraph', text }
 *   - list:        { type: 'list', items: string[], ordered?: boolean }
 *   - workflow:    { type: 'workflow', steps: string[], label?: string }
 *   - divider:     { type: 'divider' }
 *   - callout:     { type: 'callout', text, variant?: 'info'|'tip' }
 *   - demo_cta:    { type: 'demo_cta', title, description, primaryLabel, secondaryLabel }
 */

export const DOCUMENTATION_CONTENT = {
  // ═══════════════════════════════════════════════════════════════════════════
  // OVERVIEW
  // ═══════════════════════════════════════════════════════════════════════════
  'overview': {
    title: 'WebTwin Platform',
    subtitle: 'Build, manage, publish, and experience immersive digital content from the web.',
    keywords: ['overview', 'platform', 'WebTwin', 'introduction', 'getting started', 'immersive', 'XR'],
    sections: [
      {
        type: 'paragraph',
        text: 'WebTwin is a web-based platform for publishing and experiencing interactive 3D, Augmented Reality (AR), and 360° virtual experiences directly through a web browser.',
      },
      {
        type: 'paragraph',
        text: 'It provides a centralized environment where immersive content can be published and accessed through web links — without requiring specialized applications or downloads.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'What You Can Build', id: 'what-you-can-build' },
      {
        type: 'paragraph',
        text: 'WebTwin supports three types of immersive experiences:',
      },
      {
        type: 'list',
        items: [
          'Interactive 3D experiences — created using Unity and published as web-compatible builds',
          'AR experiences — created using Unity or HTML/web technologies and accessed via web links',
          '360° virtual tours — created and managed directly within the WebTwin platform',
        ],
      },
      {
        type: 'paragraph',
        text: 'These capabilities support a range of use cases including interactive environments, architectural projects, virtual spaces, AR experiences, and panoramic tours.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Core Platform Modules', id: 'core-platform-modules' },
      {
        type: 'paragraph',
        text: 'The platform is organized around three core modules, each designed for a specific type of immersive content.',
      },

      { type: 'heading', level: 3, text: 'Unity Scene', id: 'unity-scene' },
      {
        type: 'paragraph',
        text: 'The Unity Scene module is used to publish interactive 3D experiences created using Unity. A Unity project is developed first, then built into a web-compatible format (such as Unity WebGL). The resulting build is uploaded and published through WebTwin, allowing users to explore the experience directly through a web browser.',
      },
      {
        type: 'workflow',
        steps: ['Create', 'Develop', 'Build', 'Publish', 'Explore'],
        label: 'Unity Scene workflow',
      },
      {
        type: 'paragraph',
        text: 'Typical applications:',
      },
      {
        type: 'list',
        items: [
          'Interactive 3D environments',
          'Digital twins',
          'Architectural visualization',
          'Virtual facilities',
          'Interactive simulations',
          'Training environments',
          'Product and project demonstrations',
        ],
      },

      { type: 'heading', level: 3, text: 'Web URLs', id: 'web-urls' },
      {
        type: 'paragraph',
        text: 'The Web URLs module is used for publishing and accessing web-based AR experiences. AR content can be developed using Unity or HTML/web technologies. The resulting experience is hosted on a backend or web server, and WebTwin connects to the live URL — acting as a centralized entry point for accessing the AR experience.',
      },
      {
        type: 'workflow',
        steps: ['Create AR Experience', 'Build / Develop', 'Publish to Server', 'Obtain Live URL', 'Connect to WebTwin', 'Publish', 'Explore'],
        label: 'Web URLs workflow',
      },
      {
        type: 'paragraph',
        text: 'Typical applications:',
      },
      {
        type: 'list',
        items: [
          'WebAR experiences',
          'AR product demonstrations',
          'Interactive AR experiences',
        ],
      },

      { type: 'heading', level: 3, text: '360° Virtual Tours', id: '360-virtual-tours' },
      {
        type: 'paragraph',
        text: '360° panoramic content can be added to the platform and organized into virtual tours. Panoramic scenes can be connected to create a navigable experience, allowing users to move between different locations or viewpoints within the tour.',
      },
      {
        type: 'workflow',
        steps: ['Prepare 360° Content', 'Create Tour', 'Add Scenes', 'Connect Scenes', 'Configure', 'Publish', 'Explore'],
        label: '360° Tour workflow',
      },
      {
        type: 'paragraph',
        text: 'Typical applications:',
      },
      {
        type: 'list',
        items: [
          'Virtual property tours',
          'Hotels and resorts',
          'Museums and exhibitions',
          'Construction progress documentation',
          'Tourism experiences',
          'Showrooms',
          'Office and commercial-space tours',
        ],
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Key Value', id: 'key-value' },
      {
        type: 'paragraph',
        text: 'WebTwin centralizes the publishing and management of immersive digital experiences. Content is accessible through web links, removing the need for specialized applications. The platform supports the full lifecycle — from content publishing to user access — across 3D, AR, and 360° formats.',
      },
      {
        type: 'demo_cta',
        title: 'Ready to explore WebTwin?',
        description: 'See the platform in action and discover how interactive 3D, AR, and 360° experiences can work for your business.',
        primaryLabel: 'Book a Demo',
        secondaryLabel: 'Explore Projects'
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // INTERACTIVE 3D & DIGITAL TWINS
  // ═══════════════════════════════════════════════════════════════════════════
  'interactive-3d': {
    title: 'Interactive 3D & Digital Twins',
    subtitle: 'Explore lifelike 3D spaces, right in the browser.',
    keywords: ['3D', 'digital twin', 'Unity', 'WebGL', 'interactive', 'simulation', 'visualization', 'architecture', 'virtual'],
    sections: [
      {
        type: 'paragraph',
        text: 'Interactive 3D experiences allow users to explore detailed, navigable 3D environments directly in a web browser. These experiences are created using Unity and published through WebTwin as web-compatible builds.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'How It Works', id: 'how-it-works' },
      {
        type: 'paragraph',
        text: 'The interactive 3D workflow begins with developing a Unity project. Once the project is ready, it is built into a web-compatible format such as Unity WebGL. The resulting build files are then uploaded and published through WebTwin. Users can access and explore the experience through a standard web browser — no downloads or plugins required.',
      },

      { type: 'heading', level: 3, text: 'Publishing Workflow', id: 'publishing-workflow' },
      {
        type: 'workflow',
        steps: ['Create', 'Develop', 'Build', 'Publish', 'Explore'],
        label: 'Interactive 3D publishing workflow',
      },
      {
        type: 'list',
        ordered: true,
        items: [
          'Create — Design and plan the 3D experience',
          'Develop — Build the interactive environment in Unity',
          'Build — Export the project as a Unity WebGL build',
          'Publish — Upload and publish the build through WebTwin',
          'Explore — Users access the experience via a web browser',
        ],
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Unity and WebGL', id: 'unity-and-webgl' },
      {
        type: 'paragraph',
        text: 'Unity is the development platform used to create interactive 3D experiences for WebTwin. Unity WebGL is the build target that allows Unity projects to run directly in web browsers without plugins.',
      },
      {
        type: 'paragraph',
        text: 'When a Unity project is built for WebGL, it produces a set of web-compatible files that can be hosted and served through WebTwin. This enables browser-based access to complex 3D environments that would traditionally require standalone applications.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Browser-Based Access', id: 'browser-based-access' },
      {
        type: 'paragraph',
        text: 'WebTwin provides browser-based access to published 3D experiences. Once published, users can open and explore the experience through a web link. No specialized software, app installation, or plugins are required.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Use Cases', id: 'use-cases' },
      {
        type: 'paragraph',
        text: 'Interactive 3D and digital twin experiences are suited for a range of applications:',
      },
      {
        type: 'list',
        items: [
          'Digital twins — Virtual replicas of physical spaces or assets',
          'Architectural visualization — Interactive walkthroughs of designs and floor plans',
          'Virtual facilities — Explorable representations of buildings, campuses, or infrastructure',
          'Interactive simulations — Scenario-based or training-oriented 3D environments',
          'Training environments — Immersive training and onboarding experiences',
          'Product and project demonstrations — Interactive showcases for clients and stakeholders',
        ],
      },
      {
        type: 'demo_cta',
        title: 'See what an interactive digital twin can do for your business.',
        description: 'Explore a live experience with our team and see how you can bring your spaces to the web.',
        primaryLabel: 'Book a Demo',
        secondaryLabel: 'Explore Projects'
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // AR EXPERIENCES
  // ═══════════════════════════════════════════════════════════════════════════
  'ar-experiences': {
    title: 'AR Experiences',
    subtitle: 'Let clients place your products in their world — no app to install.',
    keywords: ['AR', 'augmented reality', 'WebAR', 'web URL', 'HTML', 'Unity', 'mobile', 'camera'],
    sections: [
      {
        type: 'paragraph',
        text: 'AR experiences allow users to overlay digital content onto the real world through their device\'s camera and browser. WebTwin provides a centralized entry point for publishing and accessing web-based AR content.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'How It Works', id: 'how-it-works' },
      {
        type: 'paragraph',
        text: 'AR experiences on WebTwin are managed through the Web URLs module. The AR content is developed externally — using either Unity or HTML/web technologies — and hosted on a backend or web server. WebTwin connects to the live URL of the AR experience, providing a centralized way to publish and access it.',
      },  

      { type: 'heading', level: 3, text: 'Development Options', id: 'development-options' },
      {
        type: 'list',
        items: [
          'Unity-based AR — AR experiences developed using Unity and deployed to the web',
          'HTML / Web-based AR — AR experiences built with web technologies (WebAR, HTML, JavaScript)',
        ],
      },

      { type: 'heading', level: 3, text: 'Publishing Workflow', id: 'publishing-workflow' },
      {
        type: 'workflow',
        steps: ['Create AR Experience', 'Build / Develop', 'Publish to Server', 'Obtain Live URL', 'Connect to WebTwin', 'Publish', 'Explore'],
        label: 'AR publishing workflow',
      },
      {
        type: 'list',
        ordered: true,
        items: [
          'Create — Design and plan the AR experience',
          'Build / Develop — Develop the AR content using Unity or web technologies',
          'Publish to Server — Host the experience on a backend or web server',
          'Obtain Live URL — Get the live URL of the hosted AR experience',
          'Connect to WebTwin — Add the URL to WebTwin through the Web URLs module',
          'Publish — Make the experience accessible through the platform',
          'Explore — Users access the AR experience via a web browser',
        ],
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Centralized Access', id: 'centralized-access' },
      {
        type: 'paragraph',
        text: 'While the AR experience itself is hosted externally, WebTwin acts as the centralized entry point. This allows organizations to manage and distribute access to AR content through a single platform, rather than sharing individual URLs or requiring separate applications.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Use Cases', id: 'use-cases' },
      {
        type: 'list',
        items: [
          'WebAR experiences — Browser-based AR accessed directly from a web link',
          'AR product demonstrations — Allow clients to visualize products in their own environment',
          'Interactive AR experiences — Engaging AR content for marketing, education, or training',
        ],
      },
      {
        type: 'demo_cta',
        title: 'Want to see AR in action?',
        description: 'Book a personalized demo and explore how your products and experiences can be brought to the web.',
        primaryLabel: 'Book a Demo',
        secondaryLabel: 'Explore Projects'
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // 360° VIRTUAL TOURS
  // ═══════════════════════════════════════════════════════════════════════════
  '360-tours': {
    title: '360° Virtual Tours',
    subtitle: 'Walk clients through real spaces, from anywhere.',
    keywords: ['360', 'panorama', 'virtual tour', 'panoramic', 'tour', 'hotspot', 'navigation', 'property', 'real estate'],
    sections: [
      {
        type: 'paragraph',
        text: '360° virtual tours allow users to explore real-world spaces through immersive panoramic imagery. Tours are created and managed directly within WebTwin — no external development tools are required.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'How It Works', id: 'how-it-works' },
      {
        type: 'paragraph',
        text: '360° panoramic content is uploaded to WebTwin and organized into a virtual tour. Individual panoramic scenes can be added, connected, and configured to create a navigable experience. Users can then move between different locations or viewpoints within the tour.',
      },

      { type: 'heading', level: 3, text: 'Creating a Tour', id: 'creating-a-tour' },
      {
        type: 'workflow',
        steps: ['Prepare 360° Content', 'Create Tour', 'Add Scenes', 'Connect Scenes', 'Configure', 'Publish', 'Explore'],
        label: '360° Tour creation workflow',
      },
      {
        type: 'list',
        ordered: true,
        items: [
          'Prepare 360° Content — Capture or obtain 360° panoramic images',
          'Create Tour — Start a new tour within WebTwin',
          'Add Scenes — Upload panoramic images as individual scenes',
          'Connect Scenes — Link scenes together to create navigation paths',
          'Configure — Set up the tour experience and scene order',
          'Publish — Make the tour accessible to users',
          'Explore — Users navigate the tour through a web browser',
        ],
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Built-In Management', id: 'built-in-management' },
      {
        type: 'paragraph',
        text: 'Unlike Interactive 3D and AR experiences, 360° virtual tours are created and managed entirely within WebTwin. The platform provides built-in tools for uploading panoramic content, organizing scenes, and configuring the tour experience — without requiring external development environments.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Use Cases', id: 'use-cases' },
      {
        type: 'list',
        items: [
          'Virtual property tours — Real estate walkthroughs for remote viewers',
          'Hotels and resorts — Immersive previews of rooms, facilities, and amenities',
          'Museums and exhibitions — Virtual gallery experiences',
          'Construction progress documentation — Visual records of project stages',
          'Tourism experiences — Explorable destination previews',
          'Showrooms — Virtual product and space displays',
          'Office and commercial-space tours — Remote office and facility walkthroughs',
        ],
      },
      {
        type: 'demo_cta',
        title: 'Take your space beyond static photos.',
        description: 'See how a 360° virtual tour can turn your property, showroom, facility, or project into an interactive experience.',
        primaryLabel: 'Book a Demo',
        secondaryLabel: 'Explore Projects'
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ROLE-BASED ACCESS CONTROL
  // ═══════════════════════════════════════════════════════════════════════════
  'access-control': {
    title: 'Role-Based Access Control',
    subtitle: 'Control exactly who can see what.',
    keywords: ['RBAC', 'role', 'access', 'permission', 'Uberadmin', 'Editor', 'Viewer', 'security', 'user management'],
    sections: [
      {
        type: 'paragraph',
        text: 'WebTwin includes role-based access control to manage what different users can see and do within the platform. Access levels are assigned through user roles, allowing organizations to control content visibility and platform permissions.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'User Roles', id: 'user-roles' },
      {
        type: 'paragraph',
        text: 'The platform defines three user roles, each with a different level of access:',
      },
      {
        type: 'list',
        items: [
          'Uberadmin — Full platform access, including user management and all configuration settings',
          'Editor — Access to content management features, including creating and editing scenes, tours, and other content',
          'Viewer — Access to view published content without content management capabilities',
        ],
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Access Management', id: 'access-management' },
      {
        type: 'paragraph',
        text: 'User accounts can be created and managed through the platform\'s user management interface. Each user is assigned a role that determines their level of access. This ensures that content creators, administrators, and viewers each have appropriate permissions for their responsibilities.',
      },
      {
        type: 'paragraph',
        text: 'Role-based access control is particularly important for organizations managing sensitive or in-progress content, where different team members need different levels of visibility and editing capability.',
      },
      {
        type: 'demo_cta',
        title: 'Want to see how WebTwin fits into your workflow?',
        description: 'See the platform in action and discover how interactive 3D, AR, and 360° experiences can work for your business.',
        primaryLabel: 'Book a Demo',
        secondaryLabel: 'Explore Projects'
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLISH CONTROLS
  // ═══════════════════════════════════════════════════════════════════════════
  'publish-controls': {
    title: 'Publish Controls',
    subtitle: 'Choose what\'s live, and what\'s still private.',
    keywords: ['publish', 'draft', 'live', 'visibility', 'private', 'public', 'status', 'content management'],
    sections: [
      {
        type: 'paragraph',
        text: 'WebTwin provides publish controls that allow content managers to decide when content becomes publicly accessible. Content can be prepared and reviewed before being made available to end users.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Content Visibility', id: 'content-visibility' },
      {
        type: 'paragraph',
        text: 'Content within WebTwin can exist in different states of visibility. Content that is being prepared or reviewed is not yet accessible to end users. Once content is published, it becomes available through its web link.',
      },
      {
        type: 'paragraph',
        text: 'This distinction between prepared and published content gives teams control over what is live and visible at any given time.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Publishing Workflow', id: 'publishing-workflow' },
      {
        type: 'paragraph',
        text: 'The publishing workflow allows content to be created, configured, and reviewed before it is made publicly accessible. Content managers can choose when to publish individual pieces of content, giving control over the timing and visibility of each experience.',
      },
      {
        type: 'list',
        items: [
          'Content is created and configured within the platform',
          'Content can be reviewed and adjusted before publishing',
          'Publishing makes the content accessible to users through its web link',
          'Published content can be managed and updated after publishing',
        ],
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Use Cases', id: 'use-cases' },
      {
        type: 'list',
        items: [
          'Staged releases — Prepare content in advance and publish when ready',
          'Content review — Allow team members to review before public access',
          'Controlled visibility — Manage which experiences are currently live',
        ],
      },
      {
        type: 'demo_cta',
        title: 'Want to see how WebTwin fits into your workflow?',
        description: 'See the platform in action and discover how interactive 3D, AR, and 360° experiences can work for your business.',
        primaryLabel: 'Book a Demo',
        secondaryLabel: 'Explore Projects'
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // INSTANT UPDATES
  // ═══════════════════════════════════════════════════════════════════════════
  'instant-updates': {
    title: 'Instant Updates',
    subtitle: 'Update your content anytime — it\'s live in seconds.',
    keywords: ['update', 'instant', 'live', 'real-time', 'edit', 'modify', 'content', 'refresh'],
    sections: [
      {
        type: 'paragraph',
        text: 'WebTwin allows published content to be updated at any time. Changes made to content within the platform are reflected in the live experience, keeping digital content current and accurate.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Updating Content', id: 'updating-content' },
      {
        type: 'paragraph',
        text: 'Published experiences — including 3D scenes, AR content links, and 360° tours — can be modified through the platform\'s content management interface. Updates to content are applied to the live experience, allowing teams to keep their immersive content up to date.',
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'What Can Be Updated', id: 'what-can-be-updated' },
      {
        type: 'list',
        items: [
          'Scene configurations and settings',
          'Tour scenes and navigation',
          'Web URL connections for AR experiences',
          'Content metadata and descriptions',
          'Category organization and groupings',
        ],
      },

      { type: 'divider' },

      { type: 'heading', level: 2, text: 'Keeping Experiences Current', id: 'keeping-experiences-current' },
      {
        type: 'paragraph',
        text: 'The ability to update published content means that digital experiences can evolve alongside the physical spaces or products they represent. Whether updating a digital twin to reflect a renovation, refreshing a 360° tour with new photography, or changing an AR experience URL, modifications can be made without taking content offline.',
      },
      {
        type: 'demo_cta',
        title: 'Want to see how WebTwin fits into your workflow?',
        description: 'See the platform in action and discover how interactive 3D, AR, and 360° experiences can work for your business.',
        primaryLabel: 'Book a Demo',
        secondaryLabel: 'Explore Projects'
      },
    ],
  },
};

// ─── Search Helpers ─────────────────────────────────────────────────────────────

/**
 * Flatten all searchable text for a given documentation page.
 * Returns an object { slug, title, subtitle, keywords, bodyText }.
 */
function flattenPageText(slug) {
  const page = DOCUMENTATION_CONTENT[slug];
  if (!page) return null;

  const bodyParts = [];
  for (const section of page.sections) {
    if (section.type === 'heading') bodyParts.push(section.text);
    if (section.type === 'paragraph') bodyParts.push(section.text);
    if (section.type === 'list') bodyParts.push(...section.items);
    if (section.type === 'callout') bodyParts.push(section.text);
  }

  return {
    slug,
    title: page.title,
    subtitle: page.subtitle,
    keywords: page.keywords || [],
    bodyText: bodyParts.join(' '),
  };
}

/**
 * Build a search index from all documentation pages.
 * Called once; memoize the result.
 */
let _searchIndex = null;

export function getSearchIndex() {
  if (_searchIndex) return _searchIndex;

  _searchIndex = Object.keys(DOCUMENTATION_CONTENT)
    .map(flattenPageText)
    .filter(Boolean);

  return _searchIndex;
}

/**
 * Search the documentation.
 * Returns matching results sorted by relevance (title > keyword > subtitle > body).
 *
 * @param {string} query
 * @returns {{ slug: string, title: string, subtitle: string, path: string, score: number }[]}
 */
export function searchDocumentation(query) {
  if (!query || typeof query !== 'string') return [];

  const q = query.toLowerCase().trim();
  if (q.length < 2) return [];

  const index = getSearchIndex();
  const results = [];

  // Build a flat map of slug → path from sidebar
  const pathMap = {};
  for (const group of SIDEBAR_SECTIONS) {
    for (const item of group.items) {
      pathMap[item.slug] = item.path;
    }
  }

  for (const entry of index) {
    let score = 0;

    // Title match (highest weight)
    if (entry.title.toLowerCase().includes(q)) score += 100;

    // Keyword match
    if (entry.keywords.some(kw => kw.toLowerCase().includes(q))) score += 80;

    // Subtitle match
    if (entry.subtitle.toLowerCase().includes(q)) score += 60;

    // Body text match
    if (entry.bodyText.toLowerCase().includes(q)) score += 20;

    if (score > 0) {
      results.push({
        slug: entry.slug,
        title: entry.title,
        subtitle: entry.subtitle,
        path: pathMap[entry.slug] || '/resources',
        score,
      });
    }
  }

  // Sort by score descending
  results.sort((a, b) => b.score - a.score);

  return results;
}
